import { useCallback } from "react";

import { useCombinedAlertsDataStore } from "./redux-hooks/combined-alerts-data-store/combined-alerts-data-store";
import { useUpdateModalStatus } from "../services/update-wellness-modal/update-wellness-modal.service";

/**
 * SGPD-406: dismiss handler for the TotalCare WP inline dashboard card.
 *
 * Fires the GraphQL mutation that persists `wellnessUpSellDismissedAt`
 * server-side and mirrors the new state in Redux so the card doesn't
 * briefly reappear in the same session before the next `getAlerts` refetch.
 *
 * Mutation failures are handled by the global Apollo `errorLink` in
 * `authApolloProvider.tsx` — it dispatches the standard error toast and
 * maps GraphQL errors centrally. The optimistic UI hide stays in place;
 * if the server write actually failed, the next `getAlerts` refetch will
 * return the un-dismissed state and the card will naturally reappear.
 *
 * Naming note: this hook uses the existing Redux convention `Upsell`
 * (lowercase 'u') to stay consistent with the rest of webappnextgen state.
 * The backend's GraphQL field is spelled `UpSell` (mid-cap 'S') — that
 * inconsistency is pre-existing and intentional to avoid churning the
 * 30+ files in the FE store that already use the lowercase form.
 *
 * @return {Function} `dismiss` — invoke from the card's close handler.
 */
export const useDismissWPUpsellCard = (): (() => void) => {
  const { updateWellnessModalCheck } = useUpdateModalStatus();
  const { setShouldShowWellnessUpsell } = useCombinedAlertsDataStore();

  return useCallback(() => {
    updateWellnessModalCheck();
    setShouldShowWellnessUpsell(false);
  }, [setShouldShowWellnessUpsell, updateWellnessModalCheck]);
};
